import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Select,
  message,
  DatePicker,
  Popover,
  Spin,
  Switch,
} from "antd";
import { FaArrowLeft, FaDownload, FaFilter } from "react-icons/fa";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  getCurrentFinancialYear,
  getFinancialYearOptions,
} from "../utils/financialYearUtils";
import useLatestRequest from "../utils/useLatestRequest";
import {
  REPORT_DATE_FORMAT,
  parseStoredDateRange,
  serializeDateRange,
  formatReportDate,
} from "../utils/reportDateRangeUtils";
import {
  DEFAULT_SUFFIX_OPTIONS,
  DEFAULT_SELECTED_SUFFIXES,
  selectAllDropdownRender,
} from "../utils/suffixFilterUtils";

const { RangePicker } = DatePicker;
const { Option } = Select;

const FILTERS_STORAGE_KEY = "report:sales-order:filters";

const SalesOrderReport = ({ onBack }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const [downloading, setDownloading] = useState(false);
  const [partyOptions, setPartyOptions] = useState([]);
  const [partyId, setPartyId] = useState(undefined);
  const [selectedSuffix, setSelectedSuffix] = useState(DEFAULT_SELECTED_SUFFIXES);
  const [isSummary, setIsSummary] = useState(false);
  const [grandTotal, setGrandTotal] = useState(null);
  const [filtersHydrated, setFiltersHydrated] = useState(false);

  // Restore filters on mount (Back navigation / refresh).
  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem(FILTERS_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return;

      if (typeof parsed.financialYear === "string" && parsed.financialYear) {
        setFinancialYear(parsed.financialYear);
      }
      if (typeof parsed.partyId === "string") {
        setPartyId(parsed.partyId || undefined);
      }
      if (Array.isArray(parsed.selectedSuffix)) {
        const restored = parsed.selectedSuffix.filter(Boolean);
        if (restored.length > 0) setSelectedSuffix(restored);
      }
      if (typeof parsed.isSummary === "boolean") {
        setIsSummary(parsed.isSummary);
      }
      const restoredDateRange = parseStoredDateRange(parsed.dateRange);
      if (restoredDateRange) setDateRange(restoredDateRange);
    } catch (e) {
      // Ignore bad JSON / storage issues
    } finally {
      setFiltersHydrated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist filters whenever they change.
  useEffect(() => {
    if (!filtersHydrated) return;
    try {
      const payload = {
        financialYear,
        partyId: partyId || "",
        selectedSuffix: Array.isArray(selectedSuffix) ? selectedSuffix : [],
        isSummary,
        dateRange: serializeDateRange(dateRange),
      };
      window.sessionStorage.setItem(
        FILTERS_STORAGE_KEY,
        JSON.stringify(payload),
      );
    } catch (e) {
      // Ignore quota / storage issues
    }
  }, [filtersHydrated, financialYear, partyId, selectedSuffix, isSummary, dateRange]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(
          `${apiBaseUrl}/inventoryManagement/getSOPartiesAndBrands`,
          { credentials: "include" },
        );
        const j = await res.json();
        if (!j.success) return;

        // adjust mapping based on your response structure
        const parties = (j.parties || []).map((name) => ({
          label: name,
          value: name, // value is also the name
        }));

        setPartyOptions(parties);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const beginRequest = useLatestRequest();

  const fetchData = async () => {
    const isCurrent = beginRequest();
    setLoading(true);
    setRows([]);
    setGrandTotal(null);
    try {
      let url = `${apiBaseUrl}/inventoryManagement/getSalesOrder`;
      const params = new URLSearchParams();
      params.set("financialYear", financialYear);
      params.set("isSummary", isSummary ? "true" : "false");
      if (partyId) params.set("partyName", partyId);
      const startDate = formatReportDate(dateRange?.[0]);
      const endDate = formatReportDate(dateRange?.[1]);
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      if (Array.isArray(selectedSuffix) && selectedSuffix.length > 0) {
        params.set("suffix", selectedSuffix.join(","));
      }
      const q = params.toString() ? `?${params.toString()}` : "";

      const res = await fetch(url + q, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const j = await res.json();
      if (!isCurrent()) return;
      if (j.success) {
        setRows(j.data || []);
        const basicFromTotals = j.grandTotals?.basicAmount;
        if (isSummary) {
          const gt =
            j.grandTotal != null ? j.grandTotal : basicFromTotals;
          setGrandTotal(gt != null ? Number(gt) : null);
        } else {
          setGrandTotal(
            basicFromTotals != null ? Number(basicFromTotals) : null,
          );
        }
      } else {
        console.error("getSalesOrder failed:", j.message);
        message.error("Failed to fetch sales orders");
      }
    } catch (err) {
      console.error(err);
      if (isCurrent()) message.error("Error fetching sales orders");
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  useEffect(() => {
    if (!filtersHydrated) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersHydrated, dateRange, partyId, selectedSuffix, isSummary, financialYear]);

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      params.set("financialYear", financialYear);
      params.set("excel", "true");
      params.set("isSummary", isSummary ? "true" : "false");
      if (partyId) params.set("partyName", partyId);
      const startDate = formatReportDate(dateRange?.[0]);
      const endDate = formatReportDate(dateRange?.[1]);
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      if (Array.isArray(selectedSuffix) && selectedSuffix.length > 0) {
        params.set("suffix", selectedSuffix.join(","));
      }
      const q = `?${params.toString()}`;
      const url = `${apiBaseUrl}/inventoryManagement/getSalesOrder${q}`;

      const res = await fetch(url, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) {
        message.error("Failed to download Excel");
        return;
      }
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = isSummary
        ? `Sales_Orders_Summary_${financialYear}_${moment().format("YYYY-MM-DD")}.xlsx`
        : `Sales_Orders_${financialYear}_${moment().format("YYYY-MM-DD")}.xlsx`;

      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error(err);
      message.error("Error downloading Excel");
    } finally {
      setDownloading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "Sales Order No.",
        dataIndex: "salesOrderNo",
        key: "salesOrderNo",
        width: 150,
        align: "center",
        render: (v, record) => {
          const soId = record?.salesOrderId || record?.soId;
          if (!v || !soId) {
            return <span>{v || "-"}</span>;
          }

          return (
            <span
              role="link"
              tabIndex={0}
              style={{
                color: "#1677ff",
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={() => navigate(`/sales-order/${soId}?tab=1`)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(`/sales-order/${soId}?tab=1`);
                }
              }}
              title="Open Sales Order"
            >
              {v}
            </span>
          );
        },
      },
      {
        title: "Suffix",
        dataIndex: "suffix",
        key: "suffix",
        width: 80,
        align: "center",
      },
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        width: 110,
        align: "center",
        render: (d) => (d ? moment(d).format("DD-MM-YYYY") : "-"),
      },
      {
        title: "Party Name",
        dataIndex: "partyName",
        key: "partyName",
        width: 180,
        align: "left",
      },
      {
        title: "GST No.",
        dataIndex: "gstNo",
        key: "gstNo",
        width: 155,
        align: "center",
      },
      {
        title: "Basic Amount",
        dataIndex: "basicAmount",
        key: "basicAmount",
        width: 120,
        align: "right",
        render: (v) => (v ? new Intl.NumberFormat("en-IN").format(v) : "-"),
      },
      {
        title: "From Date",
        dataIndex: "fromDate",
        key: "fromDate",
        width: 110,
        align: "center",
        render: (d) => (d ? moment(d).format("DD-MM-YYYY") : "-"),
      },
      {
        title: "To Date",
        dataIndex: "toDate",
        key: "toDate",
        width: 110,
        align: "center",
        render: (d) => (d ? moment(d).format("DD-MM-YYYY") : "-"),
      },
      {
        title: "Brand",
        dataIndex: "brand",
        key: "brand",
        width: 130,
        align: "left",
      },
      {
        title: "Salesman",
        dataIndex: "salesman",
        key: "salesman",
        width: 140,
        align: "center",
      },
      {
        title: "Category",
        dataIndex: "category",
        key: "category",
        width: 100,
        align: "center",
      },
    ],
    [navigate],
  );

  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
          }}
        />
      ),
    },
  };

  const formatAmount = (v) =>
    typeof v === "number"
      ? new Intl.NumberFormat("en-IN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(v)
      : v ?? "-";

  const summaryColumns = [
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      width: 180,
      align: "center",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 140,
      align: "center",
      render: formatAmount,
    },
  ];

  return (
    <div
      className="parent-padding-container"
      style={{ backgroundColor: "white", borderRadius: 12, padding: 20 }}
    >
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button icon={<FaArrowLeft />} onClick={onBack} type="text" />
              Sales Orders
            </Space>

            <Space wrap>
              <Switch
                checkedChildren="Summary"
                unCheckedChildren="Detailed"
                checked={isSummary}
                onChange={setIsSummary}
              />
              <Select
                value={financialYear}
                onChange={setFinancialYear}
                style={{ width: 120 }}
                placeholder="Select FY"
              >
                {getFinancialYearOptions().map((fy) => (
                  <Option key={fy} value={fy}>
                    {fy}
                  </Option>
                ))}
              </Select>
              <Select
                mode="multiple"
                placeholder="Filter by Suffix"
                allowClear
                value={selectedSuffix.length ? selectedSuffix : undefined}
                onChange={(val) => setSelectedSuffix(val ?? [])}
                options={DEFAULT_SUFFIX_OPTIONS}
                style={{ minWidth: 140 }}
                dropdownRender={selectAllDropdownRender(
                  DEFAULT_SUFFIX_OPTIONS,
                  selectedSuffix,
                  setSelectedSuffix,
                )}
              />
              <Select
                value={partyId}
                onChange={setPartyId}
                allowClear
                showSearch
                placeholder="Search by Party Name"
                style={{ width: 240 }}
                filterOption={(input, option) => {
                  const label = String(option?.label ?? "").toLowerCase();
                  const query = String(input ?? "").toLowerCase();
                  if (!query) return true;
                  return label.startsWith(query);
                }}
                options={partyOptions}
              />
              <DatePicker.RangePicker
                value={dateRange}
                onChange={setDateRange}
                format={REPORT_DATE_FORMAT}
                style={{ minWidth: 260 }}
                allowClear
              />
              <Button
                type="primary"
                icon={<FaDownload />}
                loading={downloading}
                onClick={handleDownloadExcel}
                style={{ marginLeft: 8 }}
              >
                Download Excel
              </Button>
            </Space>
          </div>
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin />
          </div>
        ) : rows.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "#999" }}>
            No sales orders found
          </div>
        ) : isSummary ? (
          <Table
            columns={summaryColumns}
            dataSource={rows}
            rowKey={(r, i) => r.month || `summary-${i}`}
            pagination={false}
            scroll={{ x: "max-content" }}
            components={components}
            size="small"
            summary={() =>
              grandTotal != null ? (
                <Table.Summary>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} align="center">
                      <strong>Grand Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="center">
                      <strong>{formatAmount(grandTotal)}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null
            }
          />
        ) : (
          <Table
            columns={columns}
            dataSource={rows}
            rowKey={(r) => `${r.salesOrderNo}-${r.suffix}`}
            pagination={false}
            scroll={{ y: 500 }}
            components={components}
            sticky
            summary={() =>
              grandTotal != null ? (
                <Table.Summary>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={5} align="center">
                      <strong>Grand Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      <strong>{formatAmount(grandTotal)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="center" />
                    <Table.Summary.Cell index={7} align="center" />
                    <Table.Summary.Cell index={8} align="center" />
                    <Table.Summary.Cell index={9} align="center" />
                    <Table.Summary.Cell index={10} align="center" />
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null
            }
          />
        )}
      </Card>
    </div>
  );
};

export default SalesOrderReport;
